import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  getChPortadores, createChPortador, updateChPortador, deleteChPortador,
  getChLiquidaciones, createChLiquidacion, getChLiquidacion, updateChLiquidacion,
  deleteChLiquidacion, cambiarEstadoLiq,
  getChCheques, cambiarEstadoCheque,
  getChFinResumen, getChFinHistorial, getChFinPorEstado,
  getFacturaCheques, cobrarTallerCheque, editarTallerCheque,
} from "../lib/api";
import {
  Button, Input, Textarea, Label, FormField, Select,
  Dialog, Sheet,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  SearchInput, PageHeader, SearchableSelect,
  StatCard, Spinner, Empty, Badge,
  Card, CardContent, CardHeader, CardTitle,
} from "../components/ui";
import {
  Plus, Edit2, Trash2, CheckCircle, XCircle,
  Landmark, Users, FileText, BarChart2, Calendar,
  ChevronDown, ChevronUp, Banknote, Eye, Printer,
} from "lucide-react";

// ─── Formato numérico ────────────────────────────────────────────────────────
function fmt(n) {
  return Number(n || 0).toLocaleString("es-PY");
}

function fmtDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date)) return d;
  return date.toLocaleDateString("es-PY");
}

// ─── Cálculos ────────────────────────────────────────────────────────────────
function calcDiferencial(valor, tasa, dias) {
  return Math.round(valor * (tasa / 100) * (dias / 30) * 100) / 100;
}

function calcTotalesLiq(cheques, gastos, tasa = 15) {
  const capital  = cheques.reduce((s, c) => s + Number(c.valor_bruto || 0), 0);
  const interes  = cheques.reduce((s, c) => s + calcDiferencial(Number(c.valor_bruto || 0), Number(tasa || 15), Number(c.dias || 30)), 0);
  const iva      = Math.round(interes * 0.10 * 100) / 100;
  const total    = Math.round((capital - interes - iva - Number(gastos || 0)) * 100) / 100;
  return { capital, interes, iva, total };
}

// ─── Badge de estado ─────────────────────────────────────────────────────────
function EstadoBadge({ estado }) {
  const map = {
    PENDIENTE: "warning",
    COBRADO:   "success",
    ANULADO:   "destructive",
  };
  return <Badge variant={map[estado] || "secondary"}>{estado}</Badge>;
}

// ─── TABS ────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "liquidaciones", label: "Liquidaciones",  icon: FileText },
  { id: "seguimiento",   label: "Seguimiento",    icon: Calendar },
  { id: "portadores",    label: "Portadores",     icon: Users },
  { id: "finanzas",      label: "Finanzas",       icon: BarChart2 },
];

function TabBar({ active, onChange }) {
  return (
    <div className="flex border-b border-border overflow-x-auto shrink-0">
      {TABS.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          onClick={() => onChange(id)}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
            active === id
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <Icon className="w-3.5 h-3.5" />
          {label}
        </button>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TAB: LIQUIDACIONES
// ═════════════════════════════════════════════════════════════════════════════
function LiquidacionesTab() {
  const [list,          setList]        = useState([]);
  const [tallerCheques, setTallerCheqs] = useState([]);
  const [portadores,    setPorts]       = useState([]);
  const [loading,       setLoading]     = useState(true);
  const [search,        setSearch]      = useState("");
  const [filtroEst,     setFiltroEst]   = useState("");
  const [open,          setOpen]        = useState(false);
  const [edit,          setEdit]        = useState(null);
  const [saving,        setSaving]      = useState(false);
  const [detalle,       setDetalle]     = useState(null);
  const [tallerDetalle, setTallerDet]   = useState(null);
  const [tallerEdit,    setTallerEdit]  = useState(null);
  const [tallerForm,    setTallerForm]  = useState({});
  const [tallerSaving,  setTallerSav]  = useState(false);

  const emptyNewPort = () => ({ nombre: "", ruc_ci: "", tipo: "PERSONA" });
  const [newPortOpen,   setNewPortOpen]   = useState(false);
  const [newPortForm,   setNewPortForm]   = useState(emptyNewPort());
  const [newPortSaving, setNewPortSaving] = useState(false);
  const sp = (k, v) => setNewPortForm((f) => ({ ...f, [k]: v }));

  const emptyForm = () => ({
    portador_id: "", portador_nombre: "", ruc_portador: "",
    tasa_interes: 15, gastos_admin: 0, observaciones: "",
    cheques: [],
  });
  const [form, setForm] = useState(emptyForm());
  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const emptyCheque = () => ({
    banco: "", agencia: "", cuenta: "", numero_cheque: "",
    emitente: "", valor_bruto: 0,
    fecha_emision: "", fecha_vencimiento: "", fecha_vencimiento_calc: "",
    dias: 30,
  });

  function addCheque() {
    setForm((f) => ({ ...f, cheques: [...f.cheques, emptyCheque()] }));
  }
  function removeCheque(i) {
    setForm((f) => ({ ...f, cheques: f.cheques.filter((_, j) => j !== i) }));
  }
  function setCheque(i, k, v) {
    setForm((f) => {
      const cheques = f.cheques.map((c, j) => {
        if (j !== i) return c;
        const upd = { ...c, [k]: v };
        upd.diferencial = calcDiferencial(Number(upd.valor_bruto || 0), Number(f.tasa_interes || 15), Number(upd.dias || 30));
        if ((k === "fecha_emision" || k === "dias") && upd.fecha_emision) {
          const [y, m, d] = upd.fecha_emision.split("-").map(Number);
          const date = new Date(y, m - 1, d + Number(upd.dias || 30));
          const calc = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}`;
          upd.fecha_vencimiento_calc = calc;
          if (!upd.fecha_vencimiento) upd.fecha_vencimiento = calc;
        }
        return upd;
      });
      return { ...f, cheques };
    });
  }

  const load = useCallback(async () => {
    try {
      const params = {};
      if (search)    params.q      = search;
      if (filtroEst) params.estado = filtroEst;
      // Map liquidacion estado to taller cheque estado
      const tallerParams = {};
      if (filtroEst === "PENDIENTE") tallerParams.estado = "RECIBIDO";
      else if (filtroEst === "COBRADO") tallerParams.estado = "COBRADO";
      const [l, p, tc] = await Promise.all([
        getChLiquidaciones(params),
        getChPortadores(),
        getFacturaCheques(tallerParams),
      ]);
      setList(l.data);
      setPorts(p.data);
      setTallerCheqs(tc.data);
    } finally {
      setLoading(false);
    }
  }, [search, filtroEst]);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEdit(null);
    setForm(emptyForm());
    setOpen(true);
  }
  async function openEdit(liq) {
    setEdit(liq);
    setOpen(true);
    setForm({ ...emptyForm(), portador_id: liq.portador_id ?? "", portador_nombre: liq.portador_nombre ?? "", ruc_portador: liq.ruc_portador ?? "", tasa_interes: liq.tasa_interes ?? 15, gastos_admin: liq.gastos_admin ?? 0, observaciones: liq.observaciones ?? "", cheques: [] });
    try {
      const { data } = await getChLiquidacion(liq.id);
      setForm({
        portador_id:     data.portador_id ?? "",
        portador_nombre: data.portador_nombre ?? "",
        ruc_portador:    data.ruc_portador ?? "",
        tasa_interes:    data.tasa_interes ?? 15,
        gastos_admin:    data.gastos_admin ?? 0,
        observaciones:   data.observaciones ?? "",
        cheques:         (data.cheques || []).map((c) => ({
          id:                    c.id,
          banco:                 c.banco ?? "",
          agencia:               c.agencia ?? "",
          cuenta:                c.cuenta ?? "",
          numero_cheque:         c.numero_cheque ?? "",
          emitente:              c.emitente ?? "",
          valor_bruto:           c.valor_bruto ?? 0,
          fecha_emision:         c.fecha_emision ? c.fecha_emision.slice(0, 10) : "",
          fecha_vencimiento:     c.fecha_vencimiento ? c.fecha_vencimiento.slice(0, 10) : "",
          fecha_vencimiento_calc:c.fecha_vencimiento_calc ? c.fecha_vencimiento_calc.slice(0, 10) : "",
          dias:                  c.dias ?? 30,
        })),
      });
    } catch {
      toast.error("Error al cargar los cheques de la liquidación");
    }
  }

  async function handleSave() {
    if (!form.cheques.length) {
      toast.error("Agregue al menos un cheque");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        portador_id:  form.portador_id || null,
        gastos_admin: Number(form.gastos_admin),
        tasa_interes: Number(form.tasa_interes),
        cheques: form.cheques.map((c) => ({
          ...c,
          valor_bruto:  Number(c.valor_bruto),
          dias:         Number(c.dias),
          tasa_interes: Number(c.tasa_interes),
          fecha_emision:         c.fecha_emision || null,
          fecha_vencimiento:     c.fecha_vencimiento || null,
          fecha_vencimiento_calc:c.fecha_vencimiento_calc || null,
        })),
      };
      if (edit) {
        await updateChLiquidacion(edit.id, payload);
        toast.success("Liquidación actualizada");
      } else {
        await createChLiquidacion(payload);
        toast.success("Liquidación creada");
      }
      setOpen(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d.type || "error de validación").join("; ")
        : (typeof detail === "string" ? detail : "Error al guardar");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleEstado(liq, estado) {
    try {
      await cambiarEstadoLiq(liq.id, estado);
      toast.success(`Liquidación marcada como ${estado}`);
      load();
    } catch {
      toast.error("Error al cambiar estado");
    }
  }

  async function handleDelete(liq) {
    if (!window.confirm(`¿Eliminar liquidación ${liq.numero}?`)) return;
    try {
      await deleteChLiquidacion(liq.id);
      toast.success("Eliminada");
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  }

  async function handleCobrarTallerLiq(ch) {
    try {
      await cobrarTallerCheque(ch.id);
      toast.success("Cheque cobrado");
      load();
    } catch {
      toast.error("Error al cobrar cheque");
    }
  }

  function openEditTaller(ch) {
    setTallerEdit(ch);
    setTallerForm({
      banco: ch.banco || "", agencia: ch.agencia || "", cuenta: ch.cuenta || "",
      numero_cheque: ch.numero_cheque || "", emitente: ch.emitente || "",
      valor: ch.valor || 0,
      fecha_emision: ch.fecha_emision ? ch.fecha_emision.slice(0, 10) : "",
      fecha_vencimiento: ch.fecha_vencimiento ? ch.fecha_vencimiento.slice(0, 10) : "",
      observaciones: ch.observaciones || "",
    });
  }

  async function handleSaveTaller() {
    setTallerSav(true);
    try {
      await editarTallerCheque(tallerEdit.id, {
        ...tallerForm,
        valor: Number(tallerForm.valor),
        fecha_emision: tallerForm.fecha_emision || null,
        fecha_vencimiento: tallerForm.fecha_vencimiento || null,
      });
      toast.success("Cheque actualizado");
      setTallerEdit(null);
      load();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setTallerSav(false);
    }
  }

  async function handleCreatePortadorInline() {
    if (!newPortForm.nombre.trim()) { toast.error("El nombre es requerido"); return; }
    setNewPortSaving(true);
    try {
      const { data: nuevo } = await createChPortador(newPortForm);
      const { data: lista } = await getChPortadores();
      setPorts(lista);
      s("portador_id", nuevo.id);
      s("portador_nombre", nuevo.nombre);
      s("ruc_portador", nuevo.ruc_ci || "");
      setNewPortOpen(false);
      setNewPortForm(emptyNewPort());
      toast.success("Portador creado y seleccionado");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al crear portador");
    } finally {
      setNewPortSaving(false);
    }
  }

  // Totales preview del formulario
  const preview = calcTotalesLiq(form.cheques, form.gastos_admin, form.tasa_interes);

  const portadorSelecto = portadores.find((p) => p.id === Number(form.portador_id));

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Controles */}
      <div className="flex flex-wrap gap-2 items-center">
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por N°, portador..."
          className="w-64"
        />
        <Select value={filtroEst} onChange={(e) => setFiltroEst(e.target.value)} className="w-40">
          <option value="">Todos</option>
          <option value="PENDIENTE">Pendiente</option>
          <option value="COBRADO">Cobrado</option>
          <option value="ANULADO">Anulado</option>
        </Select>
        <Button onClick={openCreate} className="ml-auto gap-1.5">
          <Plus className="w-4 h-4" /> Nueva Liquidación
        </Button>
      </div>

      {/* Lista */}
      {loading ? <Spinner /> : list.length === 0 ? (
        <Empty icon={FileText} title="Sin liquidaciones" description="Cree la primera liquidación de cheques" />
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N°</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Portador</TableHead>
                <TableHead>RUC/CI</TableHead>
                <TableHead className="text-right">Capital</TableHead>
                <TableHead className="text-right">Interés</TableHead>
                <TableHead className="text-right">Total a recibir</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((liq) => (
                <TableRow key={liq.id}>
                  <TableCell className="font-mono font-medium">{liq.numero}</TableCell>
                  <TableCell>{fmtDate(liq.fecha)}</TableCell>
                  <TableCell>{liq.portador_nombre || "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{liq.ruc_portador || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{fmt(liq.capital)}</TableCell>
                  <TableCell className="text-right font-mono text-orange-600">{fmt(liq.interes)}</TableCell>
                  <TableCell className="text-right font-mono font-semibold text-green-600">{fmt(liq.total_a_recibir)}</TableCell>
                  <TableCell><EstadoBadge estado={liq.estado} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setDetalle(liq)} title="Ver detalle">
                        <FileText className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(liq)} title="Editar">
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      {liq.estado === "PENDIENTE" && (
                        <Button size="sm" variant="ghost" className="text-green-600 hover:text-green-700"
                          onClick={() => handleEstado(liq, "COBRADO")} title="Marcar como cobrado">
                          <CheckCircle className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {liq.estado !== "ANULADO" && (
                        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive"
                          onClick={() => handleEstado(liq, "ANULADO")} title="Anular">
                          <XCircle className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-destructive"
                        onClick={() => handleDelete(liq)} title="Eliminar">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Cheques de Taller */}
      {tallerCheques.length > 0 && (
        <div className="mt-2">
          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Cheques de Taller</p>
          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N° Factura</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Banco / Cuenta</TableHead>
                  <TableHead>N° Cheque</TableHead>
                  <TableHead>Emitente</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Vencimiento</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {tallerCheques.map((ch) => {
                  const vencido = ch.fecha_vencimiento && new Date(ch.fecha_vencimiento) < new Date() && ch.estado === "RECIBIDO";
                  return (
                    <TableRow key={ch.id} className={vencido ? "bg-destructive/5" : ""}>
                      <TableCell className="font-mono text-xs">{ch.factura_numero}</TableCell>
                      <TableCell>{ch.cliente_nombre}</TableCell>
                      <TableCell className="text-xs">
                        <div>{ch.banco || "—"}</div>
                        <div className="text-muted-foreground">{ch.cuenta || ""}</div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{ch.numero_cheque || "—"}</TableCell>
                      <TableCell>{ch.emitente || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(ch.valor)}</TableCell>
                      <TableCell className={`text-xs ${vencido ? "text-destructive font-semibold" : ""}`}>
                        {fmtDate(ch.fecha_vencimiento)}
                        {vencido && <div className="text-[10px]">VENCIDO</div>}
                      </TableCell>
                      <TableCell>
                        <Badge variant={ch.estado === "COBRADO" ? "success" : "warning"}>
                          {ch.estado === "RECIBIDO" ? "PENDIENTE" : ch.estado}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => setTallerDet(ch)} title="Ver detalle">
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          {ch.estado !== "COBRADO" && (
                            <Button size="sm" variant="ghost" onClick={() => openEditTaller(ch)} title="Editar">
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {ch.estado === "RECIBIDO" && (
                            <Button size="sm" variant="ghost" className="text-green-600"
                              onClick={() => handleCobrarTallerLiq(ch)} title="Marcar cobrado">
                              <CheckCircle className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Sheet detalle cheque taller */}
      <Sheet open={!!tallerDetalle} onClose={() => setTallerDet(null)} title={tallerDetalle ? `Cheque — ${tallerDetalle.factura_numero}` : ""}>
        {tallerDetalle && (
          <div className="space-y-3 text-sm">
            <Card><CardContent className="pt-4 space-y-1">
              <p><span className="text-muted-foreground">Factura: </span><span className="font-mono">{tallerDetalle.factura_numero}</span></p>
              <p><span className="text-muted-foreground">Cliente: </span>{tallerDetalle.cliente_nombre}</p>
              <p><span className="text-muted-foreground">Estado: </span>
                <Badge variant={tallerDetalle.estado === "COBRADO" ? "success" : "warning"}>
                  {tallerDetalle.estado === "RECIBIDO" ? "PENDIENTE" : tallerDetalle.estado}
                </Badge>
              </p>
            </CardContent></Card>
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground text-xs">Banco</span><p>{tallerDetalle.banco || "—"}</p></div>
              <div><span className="text-muted-foreground text-xs">Cuenta</span><p>{tallerDetalle.cuenta || "—"}</p></div>
              <div><span className="text-muted-foreground text-xs">N° Cheque</span><p className="font-mono">{tallerDetalle.numero_cheque || "—"}</p></div>
              <div><span className="text-muted-foreground text-xs">Emitente</span><p>{tallerDetalle.emitente || "—"}</p></div>
              <div><span className="text-muted-foreground text-xs">Valor</span><p className="font-semibold">₲ {fmt(tallerDetalle.valor)}</p></div>
              <div><span className="text-muted-foreground text-xs">Vencimiento</span><p>{fmtDate(tallerDetalle.fecha_vencimiento)}</p></div>
            </div>
            {tallerDetalle.observaciones && <p className="text-muted-foreground text-xs">{tallerDetalle.observaciones}</p>}
          </div>
        )}
      </Sheet>

      {/* Dialog editar cheque taller */}
      <Dialog open={!!tallerEdit} onClose={() => setTallerEdit(null)} title={tallerEdit ? `Editar cheque — ${tallerEdit.factura_numero}` : ""}>
        {tallerEdit && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Banco"><Input value={tallerForm.banco} onChange={(e) => setTallerForm((f) => ({ ...f, banco: e.target.value }))} /></FormField>
              <FormField label="Agencia"><Input value={tallerForm.agencia} onChange={(e) => setTallerForm((f) => ({ ...f, agencia: e.target.value }))} /></FormField>
              <FormField label="Cuenta"><Input value={tallerForm.cuenta} onChange={(e) => setTallerForm((f) => ({ ...f, cuenta: e.target.value }))} /></FormField>
              <FormField label="N° Cheque"><Input value={tallerForm.numero_cheque} onChange={(e) => setTallerForm((f) => ({ ...f, numero_cheque: e.target.value }))} /></FormField>
              <FormField label="Emitente"><Input value={tallerForm.emitente} onChange={(e) => setTallerForm((f) => ({ ...f, emitente: e.target.value }))} /></FormField>
              <FormField label="Valor (₲)"><Input type="number" min="0" value={tallerForm.valor} onChange={(e) => setTallerForm((f) => ({ ...f, valor: e.target.value }))} /></FormField>
              <FormField label="Fecha emisión"><Input type="date" value={tallerForm.fecha_emision} onChange={(e) => setTallerForm((f) => ({ ...f, fecha_emision: e.target.value }))} /></FormField>
              <FormField label="Fecha vencimiento"><Input type="date" value={tallerForm.fecha_vencimiento} onChange={(e) => setTallerForm((f) => ({ ...f, fecha_vencimiento: e.target.value }))} /></FormField>
            </div>
            <FormField label="Observaciones"><Input value={tallerForm.observaciones} onChange={(e) => setTallerForm((f) => ({ ...f, observaciones: e.target.value }))} /></FormField>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setTallerEdit(null)}>Cancelar</Button>
              <Button onClick={handleSaveTaller} disabled={tallerSaving}>{tallerSaving ? "Guardando…" : "Guardar"}</Button>
            </div>
          </div>
        )}
      </Dialog>

      {/* Dialog Crear/Editar */}
      <Dialog open={open} onClose={() => setOpen(false)} title={edit ? `Editar ${edit.numero}` : "Nueva Liquidación de Cheques"}>
        <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-1">
          {/* Portador */}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Portador">
              <SearchableSelect
                value={form.portador_id}
                onChange={(pid) => {
                  s("portador_id", pid);
                  if (pid) {
                    const p = portadores.find((p) => p.id === Number(pid));
                    if (p) {
                      s("portador_nombre", p.nombre);
                      s("ruc_portador", p.ruc_ci || "");
                    }
                  } else {
                    s("portador_nombre", "");
                    s("ruc_portador", "");
                  }
                }}
                options={[
                  { value: "", label: "— Sin portador —" },
                  ...portadores.map((p) => ({
                    value: p.id,
                    label: p.nombre,
                    sublabel: p.ruc_ci || "",
                  })),
                ]}
                placeholder="Buscar portador…"
                onCreateNew={() => setNewPortOpen(true)}
                createNewLabel="Crear nuevo portador"
              />
            </FormField>
            <FormField label="Nombre (manual)">
              <Input value={form.portador_nombre} onChange={(e) => s("portador_nombre", e.target.value)}
                placeholder="Nombre del portador" />
            </FormField>
            <FormField label="RUC / CI">
              <Input value={form.ruc_portador} onChange={(e) => s("ruc_portador", e.target.value)} />
            </FormField>
            <FormField label="Tasa de interés (% mensual)">
              <Input type="number" value={form.tasa_interes} min={0} step={0.5}
                onChange={(e) => s("tasa_interes", e.target.value)} />
            </FormField>
            <FormField label="Gastos administrativos (Gs.)">
              <Input type="number" value={form.gastos_admin} min={0}
                onChange={(e) => s("gastos_admin", e.target.value)} />
            </FormField>
            <FormField label="Observaciones">
              <Input value={form.observaciones} onChange={(e) => s("observaciones", e.target.value)} />
            </FormField>
          </div>

          {/* Cheques */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold text-sm">Cheques</p>
              <Button size="sm" variant="outline" onClick={addCheque}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Agregar cheque
              </Button>
            </div>

            {form.cheques.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4 border border-dashed border-border rounded-md">
                Sin cheques. Haga clic en "Agregar cheque".
              </p>
            )}

            {form.cheques.map((c, i) => (
              <ChequeRow key={i} cheque={c} index={i} tasa={form.tasa_interes} onChange={setCheque} onRemove={removeCheque} />
            ))}
          </div>

          {/* Resumen de totales */}
          {form.cheques.length > 0 && (
            <div className="rounded-lg bg-muted/50 border border-border p-3 space-y-1 text-sm">
              <p className="font-semibold text-xs uppercase tracking-widest text-muted-foreground mb-2">Resumen</p>
              <div className="flex justify-between"><span>Capital</span><span className="font-mono">{fmt(preview.capital)}</span></div>
              <div className="flex justify-between text-orange-600"><span>Interés con IVA</span><span className="font-mono">{fmt(preview.interes)}</span></div>
              <div className="flex justify-between text-orange-500"><span>IVA (10%)</span><span className="font-mono">{fmt(preview.iva)}</span></div>
              <div className="flex justify-between"><span>Gastos administrativos</span><span className="font-mono">{fmt(form.gastos_admin)}</span></div>
              <div className="flex justify-between font-bold text-green-600 pt-1 border-t border-border">
                <span>Total a recibir</span>
                <span className="font-mono">{fmt(preview.total)}</span>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? "Guardando…" : edit ? "Actualizar" : "Crear"}</Button>
          </div>
        </div>
      </Dialog>

      {/* Dialog nuevo portador inline */}
      <Dialog open={newPortOpen} onClose={() => setNewPortOpen(false)} title="Nuevo Portador">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Nombre *" className="col-span-2">
              <Input value={newPortForm.nombre} onChange={(e) => sp("nombre", e.target.value)} autoFocus />
            </FormField>
            <FormField label="RUC / CI">
              <Input value={newPortForm.ruc_ci} onChange={(e) => sp("ruc_ci", e.target.value)} />
            </FormField>
            <FormField label="Tipo">
              <Select value={newPortForm.tipo} onChange={(e) => sp("tipo", e.target.value)}>
                <option value="PERSONA">Persona</option>
                <option value="EMPRESA">Empresa</option>
              </Select>
            </FormField>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setNewPortOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreatePortadorInline} disabled={newPortSaving}>
              {newPortSaving ? "Guardando…" : "Crear portador"}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Sheet detalle */}
      {detalle && (
        <DetalleSheet liq={detalle} onClose={() => setDetalle(null)} />
      )}
    </div>
  );
}

// ─── Fila de cheque en el formulario ─────────────────────────────────────────
function ChequeRow({ cheque: c, index: i, tasa = 15, onChange, onRemove }) {
  const [expanded, setExpanded] = useState(true);
  const dif = calcDiferencial(Number(c.valor_bruto || 0), Number(tasa || 15), Number(c.dias || 30));

  return (
    <div className="border border-border rounded-lg mb-2 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 cursor-pointer select-none"
        onClick={() => setExpanded((e) => !e)}>
        <Banknote className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-sm font-medium flex-1">
          Cheque #{i + 1}
          {c.numero_cheque ? ` — ${c.numero_cheque}` : ""}
          {c.emitente ? ` · ${c.emitente}` : ""}
          {c.valor_bruto ? ` · Gs. ${fmt(c.valor_bruto)}` : ""}
        </span>
        {dif > 0 && <span className="text-xs text-orange-600 font-mono">Dif: {fmt(dif)}</span>}
        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        <button onClick={(e) => { e.stopPropagation(); onRemove(i); }}
          className="p-1 hover:text-destructive text-muted-foreground">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="grid grid-cols-2 gap-3 p-3">
          <FormField label="Banco">
            <Input value={c.banco} onChange={(e) => onChange(i, "banco", e.target.value)} placeholder="Ej: Interisa" />
          </FormField>
          <FormField label="Agencia">
            <Input value={c.agencia} onChange={(e) => onChange(i, "agencia", e.target.value)} placeholder="Ej: CDE" />
          </FormField>
          <FormField label="Cuenta">
            <Input value={c.cuenta} onChange={(e) => onChange(i, "cuenta", e.target.value)} />
          </FormField>
          <FormField label="N° Cheque (WICH)">
            <Input value={c.numero_cheque} onChange={(e) => onChange(i, "numero_cheque", e.target.value)} />
          </FormField>
          <FormField label="Emitente">
            <Input value={c.emitente} onChange={(e) => onChange(i, "emitente", e.target.value)} className="col-span-2" />
          </FormField>
          <FormField label="Valor bruto (Gs.)">
            <Input type="number" value={c.valor_bruto} min={0}
              onChange={(e) => onChange(i, "valor_bruto", e.target.value)} />
          </FormField>
          <FormField label="Días">
            <Input type="number" value={c.dias} min={1}
              onChange={(e) => onChange(i, "dias", e.target.value)} />
          </FormField>
          <FormField label="Fecha emisión">
            <Input type="date" value={c.fecha_emision}
              onChange={(e) => onChange(i, "fecha_emision", e.target.value)} />
          </FormField>
          <FormField label="Vencimiento">
            <Input type="date" value={c.fecha_vencimiento}
              onChange={(e) => onChange(i, "fecha_vencimiento", e.target.value)} />
          </FormField>
          <FormField label="Vencimiento calculado">
            <Input type="date" value={c.fecha_vencimiento_calc}
              onChange={(e) => onChange(i, "fecha_vencimiento_calc", e.target.value)} />
          </FormField>
          {dif > 0 && (
            <div className="col-span-2 text-xs text-right text-orange-600 font-mono">
              Diferencial de compra: Gs. {fmt(dif)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Impresión proforma de cheque ────────────────────────────────────────────
function imprimirProforma(liq) {
  const cheques = liq.cheques || [];
  const filasCheques = cheques.map((c) => `
    <tr>
      <td>${c.banco || "—"}</td>
      <td>${c.agencia || "—"}</td>
      <td class="mono">${c.cuenta || "—"}</td>
      <td class="mono">${c.numero_cheque || "—"}</td>
      <td>${c.emitente || "—"}</td>
      <td class="tr mono">₲ ${fmt(c.valor_bruto)}</td>
      <td class="tc">${fmtDate(c.fecha_emision)}</td>
      <td class="tc">${fmtDate(c.fecha_vencimiento)}</td>
      <td class="tc">${c.dias ?? "—"}</td>
      <td class="tc">${c.tasa_interes ?? "—"}%</td>
      <td class="tr mono naranja">₲ ${fmt(c.diferencial)}</td>
    </tr>`).join("");

  const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>Proforma de Cheque — ${liq.numero}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #111; padding: 20px; }
    h1 { font-size: 18px; font-weight: bold; text-align: center; margin-bottom: 4px; }
    .sub { text-align: center; font-size: 12px; color: #555; margin-bottom: 16px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; margin-bottom: 14px; font-size: 11px; }
    .info-grid .lbl { color: #555; margin-right: 4px; }
    .badge { display: inline-block; background: #d1fae5; color: #065f46; border-radius: 9999px; padding: 1px 8px; font-size: 10px; font-weight: bold; }
    h2 { font-size: 12px; font-weight: bold; text-transform: uppercase; letter-spacing: .05em; margin: 14px 0 6px; border-bottom: 1px solid #bbb; padding-bottom: 3px; }
    table { width: 100%; border-collapse: collapse; font-size: 10px; }
    th { background: #f3f4f6; text-align: left; padding: 4px 6px; border: 1px solid #ddd; font-weight: 600; white-space: nowrap; }
    td { padding: 3px 6px; border: 1px solid #e5e7eb; }
    .tr { text-align: right; }
    .tc { text-align: center; }
    .mono { font-family: monospace; }
    .naranja { color: #c2410c; }
    .verde { color: #15803d; }
    .resumen { margin-top: 16px; border: 1px solid #bbb; border-radius: 6px; padding: 10px 14px; width: 280px; margin-left: auto; }
    .resumen .row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 11px; }
    .resumen .row.naranja { color: #c2410c; }
    .resumen .total { border-top: 2px solid #111; margin-top: 4px; padding-top: 6px; font-weight: bold; font-size: 13px; }
    .resumen .total span:last-child { color: #15803d; }
    @media print { body { padding: 10px; } }
  </style>
</head>
<body>
  <h1>Proforma de Cheque</h1>
  <p class="sub">Liquidación ${liq.numero}</p>

  <div class="info-grid">
    <div><span class="lbl">Portador:</span><strong>${liq.portador_nombre || "—"}</strong></div>
    <div><span class="lbl">RUC/CI:</span>${liq.ruc_portador || "—"}</div>
    <div><span class="lbl">Fecha:</span>${fmtDate(liq.fecha)}</div>
    <div><span class="lbl">Estado:</span><span class="badge">${liq.estado || "—"}</span></div>
    <div><span class="lbl">Tasa:</span>${liq.tasa_interes ?? "—"}% mensual</div>
  </div>

  <h2>Cheques</h2>
  <table>
    <thead>
      <tr>
        <th>Banco</th><th>Agencia</th><th>Cuenta</th><th>N° Cheque</th>
        <th>Emitente</th><th class="tr">Valor Bruto</th>
        <th class="tc">F. Emisión</th><th class="tc">Vencimiento</th>
        <th class="tc">Días</th><th class="tc">Tasa</th><th class="tr">Diferencial</th>
      </tr>
    </thead>
    <tbody>${filasCheques}</tbody>
  </table>

  <h2>Detalle de Liquidación</h2>
  <div class="resumen">
    <div class="row"><span>Capital</span><span class="mono">₲ ${fmt(liq.capital)}</span></div>
    <div class="row naranja"><span>Interés con IVA</span><span class="mono">₲ ${fmt(liq.interes)}</span></div>
    <div class="row naranja"><span>IVA (10%)</span><span class="mono">₲ ${fmt(liq.iva)}</span></div>
    <div class="row"><span>Gastos administrativos</span><span class="mono">₲ ${fmt(liq.gastos_admin)}</span></div>
    <div class="row total"><span>Total a recibir</span><span class="mono verde">₲ ${fmt(liq.total_a_recibir)}</span></div>
  </div>

  <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();}}<\/script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=900,height=650");
  w.document.write(html);
  w.document.close();
}

// ─── Sheet de detalle de liquidación ─────────────────────────────────────────
function DetalleSheet({ liq: liqBase, onClose }) {
  const [liq, setLiq] = useState(liqBase);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getChLiquidacion(liqBase.id)
      .then(({ data }) => setLiq(data))
      .catch(() => toast.error("Error al cargar el detalle"))
      .finally(() => setLoading(false));
  }, [liqBase.id]);

  return (
    <Sheet open onClose={onClose} title={`Liquidación ${liq.numero}`}>
      {loading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><span className="text-muted-foreground">Portador:</span> <span className="font-medium">{liq.portador_nombre || "—"}</span></div>
          <div><span className="text-muted-foreground">RUC/CI:</span> <span className="font-medium">{liq.ruc_portador || "—"}</span></div>
          <div><span className="text-muted-foreground">Fecha:</span> <span>{fmtDate(liq.fecha)}</span></div>
          <div><span className="text-muted-foreground">Estado:</span> <EstadoBadge estado={liq.estado} /></div>
          <div><span className="text-muted-foreground">Tasa:</span> <span>{liq.tasa_interes}% mensual</span></div>
        </div>

        {/* Tabla de cheques */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="font-semibold text-sm">Cheques</p>
            <Button size="sm" variant="outline" onClick={() => imprimirProforma(liq)}>
              <Printer className="w-3.5 h-3.5 mr-1" /> Proforma de cheque
            </Button>
          </div>
          <div className="rounded border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Banco</TableHead>
                  <TableHead>Agencia</TableHead>
                  <TableHead>Cuenta</TableHead>
                  <TableHead>N° Cheque</TableHead>
                  <TableHead>Emitente</TableHead>
                  <TableHead className="text-right">Valor bruto</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Vencto</TableHead>
                  <TableHead>Días</TableHead>
                  <TableHead>Tasa</TableHead>
                  <TableHead className="text-right">Diferencial</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(liq.cheques || []).map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.banco || "—"}</TableCell>
                    <TableCell>{c.agencia || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{c.cuenta || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{c.numero_cheque || "—"}</TableCell>
                    <TableCell>{c.emitente || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(c.valor_bruto)}</TableCell>
                    <TableCell className="text-xs">{fmtDate(c.fecha_emision)}</TableCell>
                    <TableCell className="text-xs">{fmtDate(c.fecha_vencimiento)}</TableCell>
                    <TableCell>{c.dias}</TableCell>
                    <TableCell>{c.tasa_interes}%</TableCell>
                    <TableCell className="text-right font-mono text-orange-600">{fmt(c.diferencial)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Resumen financiero */}
        <div className="rounded-lg border border-border p-3 space-y-1 text-sm">
          <p className="font-semibold text-xs uppercase tracking-widest text-muted-foreground mb-2">Detalle de Liquidación</p>
          <div className="flex justify-between"><span>Capital</span><span className="font-mono">{fmt(liq.capital)}</span></div>
          <div className="flex justify-between text-orange-600"><span>Interés con IVA</span><span className="font-mono">{fmt(liq.interes)}</span></div>
          <div className="flex justify-between text-orange-500"><span>IVA (10%)</span><span className="font-mono">{fmt(liq.iva)}</span></div>
          <div className="flex justify-between"><span>Gastos administrativos</span><span className="font-mono">{fmt(liq.gastos_admin)}</span></div>
          <div className="flex justify-between font-bold text-green-600 pt-1 border-t border-border">
            <span>Total a recibir</span>
            <span className="font-mono text-base">{fmt(liq.total_a_recibir)}</span>
          </div>
        </div>

        {liq.observaciones && (
          <p className="text-sm text-muted-foreground">{liq.observaciones}</p>
        )}
      </div>
      )}
    </Sheet>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TAB: SEGUIMIENTO DE CHEQUES
// ═════════════════════════════════════════════════════════════════════════════
function SeguimientoTab() {
  const [cheques,       setCheques]       = useState([]);
  const [tallerCheques, setTallerCheques] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [periodo,       setPeriodo]       = useState("semana");
  const [estado,        setEstado]        = useState("PENDIENTE");
  const [segDetalle,    setSegDetalle]    = useState(null);
  const [segEdit,       setSegEdit]       = useState(null);
  const [segForm,       setSegForm]       = useState({});
  const [segSaving,     setSegSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const hoy = new Date();
      let desde = null, hasta = null;
      if (periodo === "semana") {
        const lunes = new Date(hoy);
        lunes.setDate(hoy.getDate() - hoy.getDay() + 1);
        const domingo = new Date(lunes);
        domingo.setDate(lunes.getDate() + 6);
        desde = lunes.toISOString().slice(0, 10);
        hasta = domingo.toISOString().slice(0, 10);
      } else if (periodo === "mes") {
        desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().slice(0, 10);
        hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).toISOString().slice(0, 10);
      }
      const prestamoParams = {};
      if (estado) prestamoParams.estado = estado;
      if (desde)  prestamoParams.desde  = desde;
      if (hasta)  prestamoParams.hasta  = hasta;

      // Taller cheques use 'RECIBIDO'/'COBRADO' states; no ANULADO equivalent
      const TALLER_ESTADO_MAP = { PENDIENTE: "RECIBIDO", COBRADO: "COBRADO" };
      const tallerParams = {};
      if (estado && TALLER_ESTADO_MAP[estado]) tallerParams.estado = TALLER_ESTADO_MAP[estado];
      if (desde) tallerParams.desde = desde;
      if (hasta) tallerParams.hasta = hasta;
      // If estado is ANULADO (no taller equivalent), skip taller load
      const skipTaller = estado && !TALLER_ESTADO_MAP[estado];

      const [rPrest, rTaller] = await Promise.all([
        getChCheques(prestamoParams),
        skipTaller ? Promise.resolve({ data: [] }) : getFacturaCheques(tallerParams),
      ]);
      setCheques(rPrest.data);
      setTallerCheques(rTaller.data);
    } catch {
      setCheques([]);
      setTallerCheques([]);
    } finally {
      setLoading(false);
    }
  }, [periodo, estado]);

  useEffect(() => { load(); }, [load]);

  async function handleEstadoCheque(id, nuevoEstado) {
    try {
      await cambiarEstadoCheque(id, nuevoEstado);
      toast.success(`Cheque marcado como ${nuevoEstado}`);
      load();
    } catch {
      toast.error("Error al cambiar estado");
    }
  }

  async function handleCobrarTaller(id) {
    try {
      await cobrarTallerCheque(id);
      toast.success("Cheque de taller cobrado");
      load();
    } catch {
      toast.error("Error al cobrar cheque");
    }
  }

  function openSegEdit(ch) {
    setSegEdit(ch);
    setSegForm({
      banco: ch.banco || "", agencia: ch.agencia || "", cuenta: ch.cuenta || "",
      numero_cheque: ch.numero_cheque || "", emitente: ch.emitente || "",
      valor: ch.valor || 0,
      fecha_emision: ch.fecha_emision ? ch.fecha_emision.slice(0, 10) : "",
      fecha_vencimiento: ch.fecha_vencimiento ? ch.fecha_vencimiento.slice(0, 10) : "",
      observaciones: ch.observaciones || "",
    });
  }

  async function handleSaveSegEdit() {
    setSegSaving(true);
    try {
      await editarTallerCheque(segEdit.id, {
        ...segForm,
        valor: Number(segForm.valor),
        fecha_emision: segForm.fecha_emision || null,
        fecha_vencimiento: segForm.fecha_vencimiento || null,
      });
      toast.success("Cheque actualizado");
      setSegEdit(null);
      load();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSegSaving(false);
    }
  }

  const allCheques = [
    ...cheques.map((c) => ({ ...c, _tipo: "PRESTAMO" })),
    ...tallerCheques.map((c) => ({ ...c, _tipo: "TALLER", estado: c.estado === "RECIBIDO" ? "PENDIENTE" : c.estado })),
  ].sort((a, b) => {
    if (!a.fecha_vencimiento) return 1;
    if (!b.fecha_vencimiento) return -1;
    return new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento);
  });

  const vencidos = allCheques.filter((c) => {
    if (!c.fecha_vencimiento) return false;
    return new Date(c.fecha_vencimiento) < new Date() && c.estado === "PENDIENTE";
  });

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex rounded-lg border border-border overflow-hidden">
          {[
            { id: "semana", label: "Esta semana" },
            { id: "mes",    label: "Este mes" },
            { id: "todos",  label: "Todos" },
          ].map(({ id, label }) => (
            <button key={id} onClick={() => setPeriodo(id)}
              className={`px-3 py-1.5 text-sm transition-colors ${periodo === id
                ? "bg-primary text-primary-foreground font-medium"
                : "bg-transparent text-muted-foreground hover:bg-secondary"
              }`}>
              {label}
            </button>
          ))}
        </div>
        <Select value={estado} onChange={(e) => setEstado(e.target.value)} className="w-40">
          <option value="">Todos los estados</option>
          <option value="PENDIENTE">Pendiente</option>
          <option value="COBRADO">Cobrado</option>
          <option value="ANULADO">Anulado</option>
        </Select>
        <span className="text-sm text-muted-foreground ml-auto">{allCheques.length} cheque(s)</span>
      </div>

      {/* Alerta de vencidos */}
      {vencidos.length > 0 && (
        <div className="rounded-lg bg-destructive/10 border border-destructive/30 p-3 text-sm text-destructive font-medium">
          ⚠ {vencidos.length} cheque(s) vencido(s) sin cobrar
        </div>
      )}

      {loading ? <Spinner /> : allCheques.length === 0 ? (
        <Empty icon={Calendar} title="Sin cheques" description="No hay cheques en el período seleccionado" />
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Origen</TableHead>
                <TableHead>Referencia</TableHead>
                <TableHead>Portador / Cliente</TableHead>
                <TableHead>Banco / Cuenta</TableHead>
                <TableHead>N° Cheque</TableHead>
                <TableHead>Emitente</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead>Vencimiento</TableHead>
                <TableHead className="text-right">Diferencial</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {allCheques.map((c) => {
                const vencido = c.fecha_vencimiento && new Date(c.fecha_vencimiento) < new Date() && c.estado === "PENDIENTE";
                const esTaller = c._tipo === "TALLER";
                return (
                  <TableRow key={`${c._tipo}-${c.id}`} className={vencido ? "bg-destructive/5" : ""}>
                    <TableCell>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        esTaller
                          ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                          : "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                      }`}>
                        {esTaller ? "TALLER" : "PRÉSTAMO"}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {esTaller ? c.factura_numero : c.liq_numero}
                    </TableCell>
                    <TableCell className="text-sm">
                      {esTaller ? c.cliente_nombre : (c.portador || "—")}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div>{c.banco || "—"}</div>
                      <div className="text-muted-foreground">{c.cuenta || ""}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{c.numero_cheque || "—"}</TableCell>
                    <TableCell>{c.emitente || "—"}</TableCell>
                    <TableCell className="text-right font-mono">
                      {fmt(esTaller ? c.valor : c.valor_bruto)}
                    </TableCell>
                    <TableCell className={`text-xs ${vencido ? "text-destructive font-semibold" : ""}`}>
                      {fmtDate(c.fecha_vencimiento)}
                      {vencido && <div className="text-[10px]">VENCIDO</div>}
                    </TableCell>
                    <TableCell className="text-right font-mono text-orange-600">
                      {esTaller ? "—" : fmt(c.diferencial)}
                    </TableCell>
                    <TableCell><EstadoBadge estado={c.estado} /></TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        {esTaller && (
                          <Button size="sm" variant="ghost" onClick={() => setSegDetalle(c)} title="Ver detalle">
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {esTaller && c.estado === "PENDIENTE" && (
                          <Button size="sm" variant="ghost" onClick={() => openSegEdit(c)} title="Editar">
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {c.estado === "PENDIENTE" && !esTaller && (
                          <Button size="sm" variant="ghost" className="text-green-600"
                            onClick={() => handleEstadoCheque(c.id, "COBRADO")} title="Marcar cobrado">
                            <CheckCircle className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {c.estado === "PENDIENTE" && esTaller && (
                          <Button size="sm" variant="ghost" className="text-green-600"
                            onClick={() => handleCobrarTaller(c.id)} title="Cobrar cheque de taller">
                            <CheckCircle className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Sheet detalle taller (seguimiento) */}
      <Sheet open={!!segDetalle} onClose={() => setSegDetalle(null)} title={segDetalle ? `Cheque — ${segDetalle.factura_numero}` : ""}>
        {segDetalle && (
          <div className="space-y-3 text-sm">
            <Card><CardContent className="pt-4 space-y-1">
              <p><span className="text-muted-foreground">Factura: </span><span className="font-mono">{segDetalle.factura_numero}</span></p>
              <p><span className="text-muted-foreground">Cliente: </span>{segDetalle.cliente_nombre}</p>
              <p><span className="text-muted-foreground">Estado: </span><EstadoBadge estado={segDetalle.estado} /></p>
            </CardContent></Card>
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground text-xs">Banco</span><p>{segDetalle.banco || "—"}</p></div>
              <div><span className="text-muted-foreground text-xs">Cuenta</span><p>{segDetalle.cuenta || "—"}</p></div>
              <div><span className="text-muted-foreground text-xs">N° Cheque</span><p className="font-mono">{segDetalle.numero_cheque || "—"}</p></div>
              <div><span className="text-muted-foreground text-xs">Emitente</span><p>{segDetalle.emitente || "—"}</p></div>
              <div><span className="text-muted-foreground text-xs">Valor</span><p className="font-semibold">₲ {fmt(segDetalle.valor)}</p></div>
              <div><span className="text-muted-foreground text-xs">Vencimiento</span><p>{fmtDate(segDetalle.fecha_vencimiento)}</p></div>
            </div>
            {segDetalle.observaciones && <p className="text-muted-foreground text-xs">{segDetalle.observaciones}</p>}
          </div>
        )}
      </Sheet>

      {/* Dialog editar cheque taller (seguimiento) */}
      <Dialog open={!!segEdit} onClose={() => setSegEdit(null)} title={segEdit ? `Editar cheque — ${segEdit.factura_numero}` : ""}>
        {segEdit && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Banco"><Input value={segForm.banco} onChange={(e) => setSegForm((f) => ({ ...f, banco: e.target.value }))} /></FormField>
              <FormField label="Agencia"><Input value={segForm.agencia} onChange={(e) => setSegForm((f) => ({ ...f, agencia: e.target.value }))} /></FormField>
              <FormField label="Cuenta"><Input value={segForm.cuenta} onChange={(e) => setSegForm((f) => ({ ...f, cuenta: e.target.value }))} /></FormField>
              <FormField label="N° Cheque"><Input value={segForm.numero_cheque} onChange={(e) => setSegForm((f) => ({ ...f, numero_cheque: e.target.value }))} /></FormField>
              <FormField label="Emitente"><Input value={segForm.emitente} onChange={(e) => setSegForm((f) => ({ ...f, emitente: e.target.value }))} /></FormField>
              <FormField label="Valor (₲)"><Input type="number" min="0" value={segForm.valor} onChange={(e) => setSegForm((f) => ({ ...f, valor: e.target.value }))} /></FormField>
              <FormField label="Fecha emisión"><Input type="date" value={segForm.fecha_emision} onChange={(e) => setSegForm((f) => ({ ...f, fecha_emision: e.target.value }))} /></FormField>
              <FormField label="Fecha vencimiento"><Input type="date" value={segForm.fecha_vencimiento} onChange={(e) => setSegForm((f) => ({ ...f, fecha_vencimiento: e.target.value }))} /></FormField>
            </div>
            <FormField label="Observaciones"><Input value={segForm.observaciones} onChange={(e) => setSegForm((f) => ({ ...f, observaciones: e.target.value }))} /></FormField>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setSegEdit(null)}>Cancelar</Button>
              <Button onClick={handleSaveSegEdit} disabled={segSaving}>{segSaving ? "Guardando…" : "Guardar"}</Button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TAB: PORTADORES
// ═════════════════════════════════════════════════════════════════════════════
function PortadoresTab() {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [open,    setOpen]    = useState(false);
  const [edit,    setEdit]    = useState(null);
  const [saving,  setSaving]  = useState(false);

  const emptyForm = () => ({ nombre: "", ruc_ci: "", telefono: "", email: "", direccion: "", tipo: "PERSONA" });
  const [form, setForm] = useState(emptyForm());
  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const r = await getChPortadores(search || undefined);
      setList(r.data);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  function openCreate() { setEdit(null); setForm(emptyForm()); setOpen(true); }
  function openEdit(p)  { setEdit(p);   setForm({ nombre: p.nombre, ruc_ci: p.ruc_ci ?? "", telefono: p.telefono ?? "", email: p.email ?? "", direccion: p.direccion ?? "", tipo: p.tipo }); setOpen(true); }

  async function handleSave() {
    if (!form.nombre.trim()) { toast.error("El nombre es requerido"); return; }
    setSaving(true);
    try {
      if (edit) { await updateChPortador(edit.id, form); toast.success("Portador actualizado"); }
      else       { await createChPortador(form);           toast.success("Portador creado"); }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(p) {
    if (!window.confirm(`¿Eliminar portador "${p.nombre}"?`)) return;
    try { await deleteChPortador(p.id); toast.success("Eliminado"); load(); }
    catch { toast.error("Error al eliminar"); }
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex gap-2 items-center">
        <SearchInput value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar portador..." className="w-64" />
        <Button onClick={openCreate} className="ml-auto gap-1.5">
          <Plus className="w-4 h-4" /> Nuevo Portador
        </Button>
      </div>

      {loading ? <Spinner /> : list.length === 0 ? (
        <Empty icon={Users} title="Sin portadores" description="Agregue portadores de cheques" />
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>RUC / CI</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.nombre}</TableCell>
                  <TableCell>{p.ruc_ci || "—"}</TableCell>
                  <TableCell>{p.telefono || "—"}</TableCell>
                  <TableCell>{p.email || "—"}</TableCell>
                  <TableCell><Badge variant="secondary">{p.tipo}</Badge></TableCell>
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)}><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(p)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title={edit ? "Editar Portador" : "Nuevo Portador"}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Nombre *" className="col-span-2">
              <Input value={form.nombre} onChange={(e) => s("nombre", e.target.value)} autoFocus />
            </FormField>
            <FormField label="RUC / CI">
              <Input value={form.ruc_ci} onChange={(e) => s("ruc_ci", e.target.value)} />
            </FormField>
            <FormField label="Tipo">
              <Select value={form.tipo} onChange={(e) => s("tipo", e.target.value)}>
                <option value="PERSONA">Persona</option>
                <option value="EMPRESA">Empresa</option>
              </Select>
            </FormField>
            <FormField label="Teléfono">
              <Input value={form.telefono} onChange={(e) => s("telefono", e.target.value)} />
            </FormField>
            <FormField label="Email">
              <Input type="email" value={form.email} onChange={(e) => s("email", e.target.value)} />
            </FormField>
            <FormField label="Dirección" className="col-span-2">
              <Input value={form.direccion} onChange={(e) => s("direccion", e.target.value)} />
            </FormField>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? "Guardando…" : edit ? "Actualizar" : "Crear"}</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TAB: FINANZAS
// ═════════════════════════════════════════════════════════════════════════════
function FinanzasTab() {
  const [resumen,   setResumen]   = useState(null);
  const [historial, setHistorial] = useState([]);
  const [porEstado, setPorEstado] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [desde,     setDesde]     = useState("");
  const [hasta,     setHasta]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (desde) p.desde = desde;
      if (hasta) p.hasta = hasta;
      const [r, h, e] = await Promise.all([
        getChFinResumen(p),
        getChFinHistorial(p),
        getChFinPorEstado(p),
      ]);
      setResumen(r.data);
      setHistorial(h.data);
      setPorEstado(e.data);
    } finally {
      setLoading(false);
    }
  }, [desde, hasta]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Filtro fechas */}
      <div className="flex flex-wrap gap-3 items-end">
        <FormField label="Desde">
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-40" />
        </FormField>
        <FormField label="Hasta">
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-40" />
        </FormField>
        <Button variant="outline" onClick={() => { setDesde(""); setHasta(""); }}>Limpiar</Button>
      </div>

      {loading ? <Spinner /> : (
        <>
          {/* KPIs */}
          {resumen && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                <StatCard
                  title="Liquidaciones"
                  value={resumen.liquidaciones}
                  sub="Cheques efectivizados"
                  icon={Landmark}
                />
                <StatCard
                  title="Valor en cheques"
                  value={`Gs. ${fmt(resumen.capital_total)}`}
                  sub="Monto bruto recibido de portadores"
                  icon={FileText}
                />
                <StatCard
                  title="Tu comisión"
                  value={`Gs. ${fmt(resumen.interes_total)}`}
                  sub="Tu ganancia por efectivizar"
                  icon={Banknote}
                  accent
                />
                <StatCard
                  title="IVA cobrado"
                  value={`Gs. ${fmt(resumen.iva_total)}`}
                  sub="IVA sobre la comisión"
                />
                <StatCard
                  title="Entregado a portadores"
                  value={`Gs. ${fmt(resumen.entregado_total)}`}
                  sub="Efectivo que recibió el portador"
                />
              </div>

              {/* Flujo explicativo */}
              <Card className="border-l-4 border-l-orange-400">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">¿Cómo se distribuye el dinero?</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    El portador trae un cheque · vos le entregás efectivo · y te quedás con la diferencia
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-2 text-sm max-w-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Cheques recibidos</span>
                      <span className="font-mono font-semibold">Gs. {fmt(resumen.capital_total)}</span>
                    </div>
                    <div className="flex justify-between text-orange-600">
                      <span>− Comisión cobrada <span className="text-xs text-muted-foreground">(tu ganancia)</span></span>
                      <span className="font-mono font-bold">Gs. {fmt(resumen.interes_total)}</span>
                    </div>
                    {resumen.iva_total > 0 && (
                      <div className="flex justify-between text-muted-foreground">
                        <span>− IVA</span>
                        <span className="font-mono">Gs. {fmt(resumen.iva_total)}</span>
                      </div>
                    )}
                    {resumen.gastos_total > 0 && (
                      <div className="flex justify-between text-muted-foreground">
                        <span>− Gastos administrativos</span>
                        <span className="font-mono">Gs. {fmt(resumen.gastos_total)}</span>
                      </div>
                    )}
                    <div className="border-t pt-2 flex justify-between font-semibold">
                      <span>= Entregado al portador</span>
                      <span className="font-mono">Gs. {fmt(resumen.entregado_total)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Por estado */}
          {porEstado.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Por Estado</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {porEstado.map((e) => (
                    <div key={e.estado} className="text-center p-3 rounded-lg bg-muted/50">
                      <EstadoBadge estado={e.estado} />
                      <p className="text-lg font-bold mt-1">{e.cantidad}</p>
                      <p className="text-xs text-muted-foreground">Cheques: Gs. {fmt(e.capital)}</p>
                      <p className="text-xs text-orange-600 font-medium">Comisión: Gs. {fmt(e.interes)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Historial mensual */}
          {historial.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Historial Mensual</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mes</TableHead>
                      <TableHead className="text-right">Liquidaciones</TableHead>
                      <TableHead className="text-right">Valor en cheques</TableHead>
                      <TableHead className="text-right">Tu comisión</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historial.map((h) => (
                      <TableRow key={h.mes}>
                        <TableCell>{h.mes}</TableCell>
                        <TableCell className="text-right">{h.liquidaciones}</TableCell>
                        <TableCell className="text-right font-mono">Gs. {fmt(h.capital)}</TableCell>
                        <TableCell className="text-right font-mono text-orange-600 font-semibold">Gs. {fmt(h.interes)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PÁGINA PRINCIPAL
// ═════════════════════════════════════════════════════════════════════════════
export default function Prestamos() {
  const [tab, setTab] = useState("liquidaciones");

  return (
    <div className="p-4 sm:p-6 flex flex-col gap-4 h-full">
      <PageHeader
        title="Préstamos · Cheques"
        sub="Gestión de descuento de cheques y liquidaciones"
      />
      <TabBar active={tab} onChange={setTab} />
      <div className="flex-1 overflow-y-auto">
        {tab === "liquidaciones" && <LiquidacionesTab />}
        {tab === "seguimiento"   && <SeguimientoTab />}
        {tab === "portadores"    && <PortadoresTab />}
        {tab === "finanzas"      && <FinanzasTab />}
      </div>
    </div>
  );
}
